import { useEffect, useMemo } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import {
  Box,
  TextField,
  Button,
  MenuItem,
  Grid,
  FormControl,
  FormControlLabel,
  FormHelperText,
  InputLabel,
  Select,
  Checkbox,
  Chip,
  OutlinedInput,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Typography,
  Paper,
  Alert,
  Divider,
} from '@mui/material';
import SaveIcon from '@mui/icons-material/Save';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import HttpIcon from '@mui/icons-material/Http';
import SecurityIcon from '@mui/icons-material/Security';
import NotificationsIcon from '@mui/icons-material/Notifications';
import { HeadersEditor, HeaderRow, headersToRecord, recordToHeaders } from './HeadersEditor';
import { UpgradePrompt } from '../ui/UpgradePrompt';
import { useAuth } from '../../lib/AuthContext';

// ============================================================================
// Types and Schemas
// ============================================================================

/**
 * Monitor types enum
 */
const MonitorTypeEnum = z.enum(['HTTP', 'PING', 'TCP', 'KEYWORD']);
type MonitorType = z.infer<typeof MonitorTypeEnum>;

/**
 * Keyword type enum
 */
const KeywordTypeEnum = z.enum(['CONTAINS', 'NOT_CONTAINS']);
type KeywordType = z.infer<typeof KeywordTypeEnum>;

/**
 * Form validation schema (name is optional - will be auto-generated from URL on create)
 */
const monitorFormSchema = z
  .object({
    name: z.string().max(100, 'Name must be 100 characters or less').optional(),
    type: MonitorTypeEnum,
    url: z.string().min(1, 'URL is required'),
    interval: z.number().int().min(30).max(3600),
    timeout: z.number().int().min(5).max(120),
    expectedStatus: z.array(z.number().int().min(100).max(599)),
    headers: z.record(z.string(), z.string()).optional(),
    keyword: z.string().optional(),
    keywordType: KeywordTypeEnum.optional(),
    checkSsl: z.boolean(),
    sslExpiryWarning: z.number().int().min(1).max(90),
    failureThreshold: z.number().int().min(1).max(10),
  })
  .refine(
    (data) => {
      // Validate URL format for HTTP/KEYWORD types
      if (['HTTP', 'KEYWORD'].includes(data.type)) {
        // Accept URLs with or without protocol (we'll add https:// if missing)
        const urlToTest = /^https?:\/\//i.test(data.url)
          ? data.url
          : `https://${data.url}`;
        try {
          const url = new URL(urlToTest);
          return ['http:', 'https:'].includes(url.protocol);
        } catch {
          return false;
        }
      }
      // For PING and TCP, just require non-empty
      return data.url.length > 0;
    },
    {
      message: 'Please enter a valid URL (e.g., example.com)',
      path: ['url'],
    }
  )
  .refine(
    (data) => {
      // Keyword is required for KEYWORD type
      if (data.type === 'KEYWORD') {
        return !!data.keyword && data.keyword.trim().length > 0;
      }
      return true;
    },
    {
      message: 'Keyword is required for keyword monitoring',
      path: ['keyword'],
    }
  );

type MonitorFormData = z.infer<typeof monitorFormSchema>;

/**
 * Initial data type (from API)
 */
export interface MonitorInitialData {
  id: string;
  name: string;
  type: MonitorType;
  url: string;
  interval: number;
  timeout: number;
  expectedStatus: number[];
  headers: Record<string, string> | null;
  keyword: string | null;
  keywordType: KeywordType | null;
  checkSsl: boolean;
  sslExpiryWarning: number;
  failureThreshold: number;
}

/**
 * Submit data type (for API)
 */
export interface MonitorSubmitData {
  name?: string;
  type: MonitorType;
  url: string;
  interval: number;
  timeout: number;
  expectedStatus: number[];
  headers?: Record<string, string>;
  keyword?: string;
  keywordType?: KeywordType;
  checkSsl: boolean;
  sslExpiryWarning: number;
  failureThreshold: number;
}

/**
 * MonitorForm component props
 */
interface MonitorFormProps {
  mode: 'create' | 'edit';
  initialData?: MonitorInitialData;
  onSubmit: (data: MonitorSubmitData) => void;
  onCancel: () => void;
  isLoading?: boolean;
  error?: string | null;
}

// ============================================================================
// Constants
// ============================================================================

const MONITOR_TYPES: { value: MonitorType; label: string; description: string }[] = [
  { value: 'HTTP', label: 'HTTP(S)', description: 'Monitor HTTP/HTTPS endpoints with optional SSL checks' },
  { value: 'PING', label: 'Ping', description: 'Check host availability via ICMP' },
  { value: 'TCP', label: 'TCP', description: 'Check TCP port connectivity' },
  { value: 'KEYWORD', label: 'Keyword', description: 'Check for specific content on page' },
];

const INTERVALS: { value: number; label: string }[] = [
  { value: 30, label: 'Every 30 seconds' },
  { value: 60, label: 'Every 1 minute' },
  { value: 120, label: 'Every 2 minutes' },
  { value: 300, label: 'Every 5 minutes' },
  { value: 600, label: 'Every 10 minutes' },
  { value: 1800, label: 'Every 30 minutes' },
  { value: 3600, label: 'Every 1 hour' },
];

const TIMEOUTS: { value: number; label: string }[] = [
  { value: 5, label: '5 seconds' },
  { value: 10, label: '10 seconds' },
  { value: 15, label: '15 seconds' },
  { value: 30, label: '30 seconds' },
  { value: 60, label: '60 seconds' },
  { value: 120, label: '120 seconds' },
];

const COMMON_STATUS_CODES: number[] = [
  200, 201, 202, 204, 301, 302, 304, 400, 401, 403, 404, 500, 502, 503,
];

const KEYWORD_TYPES: { value: KeywordType; label: string }[] = [
  { value: 'CONTAINS', label: 'Page contains keyword' },
  { value: 'NOT_CONTAINS', label: 'Page does NOT contain keyword' },
];

// Minimum interval for free plan (5 minutes = 300 seconds)
const FREE_PLAN_MIN_INTERVAL = 300;

// ============================================================================
// Helper Functions
// ============================================================================

/**
 * Get default form values
 */
function getDefaultValues(initialData?: MonitorInitialData, isFreePlan?: boolean): MonitorFormData {
  if (initialData) {
    return {
      name: initialData.name,
      type: initialData.type,
      url: initialData.url,
      interval: initialData.interval,
      timeout: initialData.timeout,
      expectedStatus: initialData.expectedStatus,
      headers: initialData.headers || undefined,
      keyword: initialData.keyword || undefined,
      keywordType: initialData.keywordType || undefined,
      checkSsl: initialData.checkSsl,
      sslExpiryWarning: initialData.sslExpiryWarning,
      failureThreshold: initialData.failureThreshold,
    };
  }

  // Default to 5 minutes for free plan, 1 minute for paid plans
  const defaultInterval = isFreePlan ? FREE_PLAN_MIN_INTERVAL : 60;

  return {
    name: '',
    type: 'HTTP',
    url: '',
    interval: defaultInterval,
    timeout: 30,
    expectedStatus: [200],
    headers: undefined,
    keyword: undefined,
    keywordType: undefined,
    checkSsl: true,
    sslExpiryWarning: 14,
    failureThreshold: 1,
  };
}

/**
 * Normalize URL by adding https:// prefix if missing
 */
function normalizeUrl(url: string, type: MonitorType): string {
  if (!['HTTP', 'KEYWORD'].includes(type)) {
    return url;
  }

  const trimmed = url.trim();
  if (!trimmed) return trimmed;

  // If URL already has a protocol, return as-is
  if (/^https?:\/\//i.test(trimmed)) {
    return trimmed;
  }

  // Add https:// prefix
  return `https://${trimmed}`;
}

/**
 * Get URL placeholder based on monitor type
 */
function getUrlPlaceholder(type: MonitorType): string {
  switch (type) {
    case 'HTTP':
    case 'KEYWORD':
      return 'https://example.com';
    case 'PING':
      return 'example.com or 192.168.1.1';
    case 'TCP':
      return 'example.com:443';
    default:
      return 'https://example.com';
  }
}

/**
 * Get URL helper text based on monitor type
 */
function getUrlHelperText(type: MonitorType): string {
  switch (type) {
    case 'HTTP':
      return 'Enter the URL to monitor (https:// added automatically if missing)';
    case 'PING':
      return 'Enter hostname or IP address to ping';
    case 'TCP':
      return 'Enter hostname:port (e.g., example.com:443)';
    case 'KEYWORD':
      return 'Enter the URL to check for keyword (https:// added automatically if missing)';
    default:
      return 'Enter the URL to monitor';
  }
}

// ============================================================================
// Component
// ============================================================================

/**
 * MonitorForm component
 * Reusable form for creating and editing monitors
 */
export function MonitorForm({
  mode,
  initialData,
  onSubmit,
  onCancel,
  isLoading = false,
  error,
}: MonitorFormProps) {
  const { user } = useAuth();
  const isFreePlan = !user?.plan || user.plan.toUpperCase() === 'FREE';

  const {
    control,
    handleSubmit,
    watch,
    setValue,
    formState: { errors, isDirty },
  } = useForm<MonitorFormData>({
    resolver: zodResolver(monitorFormSchema),
    defaultValues: getDefaultValues(initialData, isFreePlan),
  });

  const monitorType = watch('type');
  const checkSsl = watch('checkSsl');
  const selectedInterval = watch('interval');

  // Track headers separately for the HeadersEditor component
  const headersValue = watch('headers') as Record<string, string> | undefined;
  const headerRows = useMemo(() => recordToHeaders(headersValue), [headersValue]);

  // Show HTTP-specific fields
  const showHttpFields = ['HTTP', 'KEYWORD'].includes(monitorType);

  // Show SSL fields for HTTP type (applies when using HTTPS URLs)
  const showSslFields = monitorType === 'HTTP';

  // Show keyword fields
  const showKeywordFields = monitorType === 'KEYWORD';

  // Auto-set keywordType when type changes to KEYWORD
  useEffect(() => {
    if (monitorType === 'KEYWORD') {
      const currentKeywordType = watch('keywordType');
      if (!currentKeywordType) {
        setValue('keywordType', 'CONTAINS', { shouldDirty: false });
      }
    }
  }, [monitorType, watch, setValue]);

  const handleHeadersChange = (newHeaders: HeaderRow[]) => {
    const record = headersToRecord(newHeaders);
    setValue('headers', Object.keys(record).length > 0 ? record : undefined, {
      shouldDirty: true,
    });
  };

  const onFormSubmit = (data: MonitorFormData) => {
    // Normalize URL (add https:// if missing for HTTP/KEYWORD types)
    const normalizedUrl = normalizeUrl(data.url, data.type);

    // Enforce interval minimum for free plan
    const interval = isFreePlan && data.interval < FREE_PLAN_MIN_INTERVAL
      ? FREE_PLAN_MIN_INTERVAL
      : data.interval;

    // Transform form data to API format
    const submitData: MonitorSubmitData = {
      type: data.type,
      url: normalizedUrl,
      interval,
      timeout: data.timeout,
      expectedStatus: data.expectedStatus,
      checkSsl: data.checkSsl,
      sslExpiryWarning: data.sslExpiryWarning,
      failureThreshold: data.failureThreshold,
    };

    // Only include name if provided (required for edit, optional for create)
    if (data.name && data.name.trim()) {
      submitData.name = data.name;
    }

    // Only include optional fields if they have values
    if (data.headers && Object.keys(data.headers).length > 0) {
      submitData.headers = data.headers as Record<string, string>;
    }

    if (data.keyword) {
      submitData.keyword = data.keyword;
    }

    if (data.keywordType) {
      submitData.keywordType = data.keywordType;
    }

    onSubmit(submitData);
  };

  return (
    <Paper sx={{ p: 4, maxWidth: 800 }}>
      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      <Box component="form" onSubmit={handleSubmit(onFormSubmit)}>
        {/* Basic Settings Section */}
        <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
          Basic Settings
        </Typography>

        <Grid container spacing={3}>
          {/* Name - only shown in edit mode */}
          {mode === 'edit' && (
            <Grid item xs={12}>
              <Controller
                name="name"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    fullWidth
                    label="Monitor Name"
                    placeholder="My Website"
                    error={!!errors.name}
                    helperText={errors.name?.message || 'A friendly name to identify this monitor (defaults to domain)'}
                    disabled={isLoading}
                  />
                )}
              />
            </Grid>
          )}

          {/* Type */}
          <Grid item xs={12} sm={6}>
            <Controller
              name="type"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  select
                  fullWidth
                  label="Monitor Type"
                  error={!!errors.type}
                  helperText={errors.type?.message}
                  disabled={isLoading}
                >
                  {MONITOR_TYPES.map((type) => (
                    <MenuItem key={type.value} value={type.value}>
                      <Box>
                        <Typography variant="body2">{type.label}</Typography>
                        <Typography variant="caption" color="text.secondary">
                          {type.description}
                        </Typography>
                      </Box>
                    </MenuItem>
                  ))}
                </TextField>
              )}
            />
          </Grid>

          {/* URL */}
          <Grid item xs={12}>
            <Controller
              name="url"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  fullWidth
                  label="URL / Host"
                  placeholder={getUrlPlaceholder(monitorType)}
                  error={!!errors.url}
                  helperText={errors.url?.message || getUrlHelperText(monitorType)}
                  disabled={isLoading}
                />
              )}
            />
          </Grid>

          {/* Interval */}
          <Grid item xs={12} sm={6}>
            <Controller
              name="interval"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  select
                  fullWidth
                  label="Check Interval"
                  error={!!errors.interval}
                  helperText={errors.interval?.message}
                  disabled={isLoading}
                  onChange={(e) => field.onChange(Number(e.target.value))}
                >
                  {INTERVALS.map((interval) => (
                    <MenuItem
                      key={interval.value}
                      value={interval.value}
                      disabled={isFreePlan && interval.value < FREE_PLAN_MIN_INTERVAL}
                    >
                      {interval.label}
                      {isFreePlan && interval.value < FREE_PLAN_MIN_INTERVAL && ' (Pro)'}
                    </MenuItem>
                  ))}
                </TextField>
              )}
            />
            {/* Upgrade prompt for fast intervals */}
            {isFreePlan && selectedInterval < FREE_PLAN_MIN_INTERVAL && (
              <UpgradePrompt
                message="Intervals faster than 5 minutes require a Pro plan."
                sx={{ mt: 1 }}
              />
            )}
          </Grid>

          {/* Timeout */}
          <Grid item xs={12} sm={6}>
            <Controller
              name="timeout"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  select
                  fullWidth
                  label="Timeout"
                  error={!!errors.timeout}
                  helperText={errors.timeout?.message || 'Request timeout before marking as failed'}
                  disabled={isLoading}
                  onChange={(e) => field.onChange(Number(e.target.value))}
                >
                  {TIMEOUTS.map((timeout) => (
                    <MenuItem key={timeout.value} value={timeout.value}>
                      {timeout.label}
                    </MenuItem>
                  ))}
                </TextField>
              )}
            />
          </Grid>
        </Grid>

        <Divider sx={{ my: 4 }} />

        {/* HTTP Settings Accordion */}
        {showHttpFields && (
          <Accordion sx={{ mb: 2 }}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <HttpIcon color="primary" />
                <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                  HTTP Settings
                </Typography>
              </Box>
            </AccordionSummary>
            <AccordionDetails>
              <Grid container spacing={3}>
                {/* Expected Status Codes */}
                <Grid item xs={12}>
                  <Controller
                    name="expectedStatus"
                    control={control}
                    render={({ field }) => (
                      <FormControl fullWidth error={!!errors.expectedStatus}>
                        <InputLabel>Expected Status Codes</InputLabel>
                        <Select
                          {...field}
                          multiple
                          input={<OutlinedInput label="Expected Status Codes" />}
                          renderValue={(selected) => (
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                              {selected.map((value) => (
                                <Chip key={value} label={value} size="small" />
                              ))}
                            </Box>
                          )}
                          disabled={isLoading}
                        >
                          {COMMON_STATUS_CODES.map((code) => (
                            <MenuItem key={code} value={code}>
                              {code} - {getStatusDescription(code)}
                            </MenuItem>
                          ))}
                        </Select>
                        <FormHelperText>
                          {errors.expectedStatus?.message ||
                            'Response codes that indicate the monitor is UP'}
                        </FormHelperText>
                      </FormControl>
                    )}
                  />
                </Grid>

                {/* Custom Headers */}
                <Grid item xs={12}>
                  <HeadersEditor
                    headers={headerRows}
                    onChange={handleHeadersChange}
                    disabled={isLoading}
                  />
                </Grid>
              </Grid>
            </AccordionDetails>
          </Accordion>
        )}

        {/* Keyword Settings Accordion */}
        {showKeywordFields && (
          <Accordion defaultExpanded sx={{ mb: 2 }}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                  Keyword Settings
                </Typography>
              </Box>
            </AccordionSummary>
            <AccordionDetails>
              <Grid container spacing={3}>
                {/* Keyword */}
                <Grid item xs={12} sm={8}>
                  <Controller
                    name="keyword"
                    control={control}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        value={field.value || ''}
                        fullWidth
                        label="Keyword"
                        placeholder="Enter text to search for"
                        error={!!errors.keyword}
                        helperText={errors.keyword?.message || 'Text to search for on the page'}
                        disabled={isLoading}
                      />
                    )}
                  />
                </Grid>

                {/* Keyword Type */}
                <Grid item xs={12} sm={4}>
                  <Controller
                    name="keywordType"
                    control={control}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        value={field.value || 'CONTAINS'}
                        select
                        fullWidth
                        label="Match Type"
                        error={!!errors.keywordType}
                        helperText={errors.keywordType?.message}
                        disabled={isLoading}
                      >
                        {KEYWORD_TYPES.map((type) => (
                          <MenuItem key={type.value} value={type.value}>
                            {type.label}
                          </MenuItem>
                        ))}
                      </TextField>
                    )}
                  />
                </Grid>
              </Grid>
            </AccordionDetails>
          </Accordion>
        )}

        {/* SSL Settings Accordion */}
        {showSslFields && (
          <Accordion sx={{ mb: 2 }}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <SecurityIcon color="primary" />
                <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                  SSL Certificate Settings
                </Typography>
              </Box>
            </AccordionSummary>
            <AccordionDetails>
              <Grid container spacing={3}>
                {/* Check SSL */}
                <Grid item xs={12}>
                  <Controller
                    name="checkSsl"
                    control={control}
                    render={({ field }) => (
                      <FormControlLabel
                        control={<Checkbox {...field} checked={field.value} disabled={isLoading} />}
                        label="Monitor SSL certificate expiration"
                      />
                    )}
                  />
                </Grid>

                {/* SSL Expiry Warning */}
                {checkSsl && (
                  <Grid item xs={12} sm={6}>
                    <Controller
                      name="sslExpiryWarning"
                      control={control}
                      render={({ field }) => (
                        <TextField
                          {...field}
                          type="number"
                          fullWidth
                          label="Expiry Warning (days)"
                          error={!!errors.sslExpiryWarning}
                          helperText={
                            errors.sslExpiryWarning?.message ||
                            'Alert when certificate expires within this many days'
                          }
                          disabled={isLoading}
                          inputProps={{ min: 1, max: 90 }}
                          onChange={(e) => field.onChange(Number(e.target.value))}
                        />
                      )}
                    />
                  </Grid>
                )}
              </Grid>
            </AccordionDetails>
          </Accordion>
        )}

        {/* Notification Settings Accordion */}
        <Accordion sx={{ mb: 2 }}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <NotificationsIcon color="primary" />
              <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                Notification Settings
              </Typography>
            </Box>
          </AccordionSummary>
          <AccordionDetails>
            <Grid container spacing={3}>
              {/* Failure Threshold */}
              <Grid item xs={12} sm={6}>
                <Controller
                  name="failureThreshold"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      type="number"
                      fullWidth
                      label="Failure Threshold"
                      error={!!errors.failureThreshold}
                      helperText={
                        errors.failureThreshold?.message ||
                        'Number of consecutive failures before alerting (1-10)'
                      }
                      disabled={isLoading}
                      inputProps={{ min: 1, max: 10 }}
                      onChange={(e) => field.onChange(Number(e.target.value))}
                    />
                  )}
                />
              </Grid>
            </Grid>
          </AccordionDetails>
        </Accordion>

        <Divider sx={{ my: 3 }} />

        {/* Action Buttons */}
        <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
          <Button variant="outlined" onClick={onCancel} disabled={isLoading}>
            Cancel
          </Button>
          <Button
            type="submit"
            variant="contained"
            startIcon={<SaveIcon />}
            disabled={isLoading || (!isDirty && mode === 'edit')}
          >
            {isLoading
              ? mode === 'create'
                ? 'Creating...'
                : 'Saving...'
              : mode === 'create'
                ? 'Create Monitor'
                : 'Save Changes'}
          </Button>
        </Box>
      </Box>
    </Paper>
  );
}

/**
 * Get human-readable description for HTTP status code
 */
function getStatusDescription(code: number): string {
  const descriptions: Record<number, string> = {
    200: 'OK',
    201: 'Created',
    202: 'Accepted',
    204: 'No Content',
    301: 'Moved Permanently',
    302: 'Found',
    304: 'Not Modified',
    400: 'Bad Request',
    401: 'Unauthorized',
    403: 'Forbidden',
    404: 'Not Found',
    500: 'Internal Server Error',
    502: 'Bad Gateway',
    503: 'Service Unavailable',
  };
  return descriptions[code] || 'Unknown';
}

export default MonitorForm;
